import React, { useEffect, useState } from 'react';
import { Clock, Video, X, Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { getRecentRooms, forgetRoom, clearRecentRooms, startFreshRoom, rememberRoom } from '@/lib/recentRooms';

/**
 * RecentRoomsPanel
 * Shows the admin's recent studio rooms with one-click re-enter buttons.
 * Admin-only. Renders nothing when the list is empty (except an empty-state
 * hint after a fresh install).
 */
const timeAgo = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return '';
  }
};

const RecentRoomsPanel = ({ className = '', compact = false, onEnter, testid = 'recent-rooms-panel', floating = false }) => {
  const { isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  const [rooms, setRooms] = useState([]);
  const [expanded, setExpanded] = useState(!floating);

  useEffect(() => {
    setRooms(getRecentRooms());
    // Refresh when the tab regains focus (in case another tab added a room)
    const onFocus = () => setRooms(getRecentRooms());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (!isAdminUser) return null;

  const handleReenter = (room) => {
    // Bump this room to the top of the list before navigating
    rememberRoom(room.code, { role: room.role });
    if (onEnter) onEnter(room);
    const path = room.role === 'host' ? `/video-room/${room.code}/host` : `/video-room/${room.code}`;
    window.location.href = path;
  };

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code ${code} copied`);
    } catch {
      toast.info(code);
    }
  };

  const handleForget = (code) => {
    forgetRoom(code);
    setRooms(getRecentRooms());
  };

  const handleClearAll = () => {
    clearRecentRooms();
    setRooms([]);
    toast.success('Room history cleared');
  };

  const handleStartFresh = () => {
    startFreshRoom('host');
  };

  // Floating collapsed pill
  if (floating && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        data-testid={`${testid}-toggle`}
        className="fixed top-24 right-4 z-40 px-3 py-2 rounded-full bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-bold flex items-center gap-2 shadow-lg"
      >
        <Clock className="w-4 h-4" />
        Recent Rooms {rooms.length > 0 && `(${rooms.length})`}
      </button>
    );
  }

  return (
    <div
      className={`rounded-2xl border-2 border-yellow-500/40 bg-gradient-to-br from-yellow-500/5 to-amber-500/5 p-4 ${floating ? 'fixed top-24 right-4 z-40 w-80 bg-black/95 backdrop-blur-md shadow-2xl' : ''} ${className}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-4 h-4 text-yellow-400 shrink-0" />
          <h3 className="font-bold text-yellow-300 text-sm uppercase tracking-wider truncate">
            Recent Rooms
          </h3>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={handleStartFresh}
            data-testid="recent-rooms-fresh-btn"
            className="px-2.5 py-1.5 rounded-md bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-bold flex items-center gap-1"
          >
            <Video className="w-3 h-3" /> New
          </button>
          {rooms.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              data-testid="recent-rooms-clear-btn"
              className="p-1.5 rounded-md bg-black/40 hover:bg-black/60 text-white/60 hover:text-white"
              title="Clear all"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {floating && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              data-testid={`${testid}-collapse`}
              className="p-1.5 rounded-md bg-black/40 hover:bg-black/60 text-white/60 hover:text-white"
              title="Collapse"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {rooms.length === 0 ? (
        <p className="text-xs text-white/50">
          No recent rooms yet. Click <span className="text-yellow-300 font-bold">New</span> to start one — it'll appear here next time.
        </p>
      ) : (
        <ul className={`space-y-1.5 ${compact ? 'max-h-56 overflow-y-auto pr-1' : ''}`}>
          {rooms.map((room) => (
            <li
              key={room.code}
              className="flex items-center gap-2 p-2 rounded-lg bg-black/30 hover:bg-black/50 border border-white/5 group"
              data-testid={`recent-room-${room.code}`}
            >
              <button
                type="button"
                onClick={() => handleReenter(room)}
                data-testid={`recent-room-enter-${room.code}`}
                className="flex-1 flex items-center gap-2 min-w-0 text-left"
              >
                <span className="font-mono font-bold text-yellow-300 text-sm shrink-0">
                  {room.code}
                </span>
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  room.role === 'host' ? 'bg-green-500/20 text-green-300' : 'bg-blue-500/20 text-blue-300'
                }`}>
                  {room.role}
                </span>
                <span className="text-[10px] text-white/40 ml-auto shrink-0">
                  {timeAgo(room.createdAt)}
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleCopy(room.code)}
                data-testid={`recent-room-copy-${room.code}`}
                className="p-1.5 rounded bg-white/5 hover:bg-white/15 text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                title="Copy code"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleForget(room.code)}
                data-testid={`recent-room-forget-${room.code}`}
                className="p-1.5 rounded bg-white/5 hover:bg-red-500/40 text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RecentRoomsPanel;
